import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DefaultButton, FlexStack, Icon } from '~/components/atomic';
import share_top_character from '~/assets/img/chat/share/share_top_character.png';
import { useGroupChatStore } from '~/store';
import useToast from '~/store/useToast';
import IconButtonWrapper from '../IconButtonWrapper/IconButtonWrapper';

export default function ChatShareMessage() {
  const { showChatShareOpen, setShowChatShareOpen } = useGroupChatStore(
    (state) => ({
      showChatShareOpen: state.showChatShareOpen,
      setShowChatShareOpen: state.setShowChatShareOpen,
    }),
  );
  const { showToast } = useToast();

  const nowUrl = window.location.href;
  const InAppLink = import.meta.env.VITE_SHARE_FRONT_SHARE_LINK + nowUrl;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const fcmToken = sessionStorage.getItem('appToken');

  const handleAddressCopy = async () => {
    try {
      await navigator.clipboard.writeText(nowUrl);
      setCopySuccess(true);
      showToast('success', 'Copied to clipboard');
    } catch {
      setCopySuccess(false);
    }
  };

  const handleShareClick = async () => {
    const userAgent = window.navigator.userAgent;
    console.log('userAgent', userAgent.includes(''));

    if (userAgent.includes('heyDApp')) {
      try {
        window.flutter_inappwebview.callHandler('share', InAppLink);
      } catch (error) {
        console.error('Flutter share failed:', error);
      }
      return;
    }

    if (navigator?.share) {
      try {
        await navigator.share({
          url: nowUrl,
          title: 'group Chat',
        });
      } catch (error) {
        console.error('Sharing failed:', error);
        await handleAddressCopy(); // 공유 실패 시에만 클립보드 복사
      }
    } else {
      await handleAddressCopy(); // 지원되지 않으면 클립보드 복사
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setShowChatShareOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setShowChatShareOpen(false);
    };
  }, []);

  return (
    <>
      {showChatShareOpen && (
        <Container>
          <Wrapper ref={wrapperRef}>
            <FlexStack>
              <div
                css={css`
                  margin: 0 auto;
                `}
              >
                <img src={share_top_character} alt='share character' />
              </div>
              <h3
                css={css`
                  color: var(--content_light, #fff);
                  font-family: Poppins;
                  font-size: 18px;
                  font-style: normal;
                  font-weight: 600;
                  letter-spacing: -0.36px;
                  line-height: 140%; /* 25.2px */
                  text-align: center;
                `}
              >
                Invite a friend to Group chat
              </h3>
              <p
                css={css`
                  color: var(--content_light_2, #efeff0);
                  font-family: Poppins;
                  font-size: 14px;
                  font-style: normal;
                  font-weight: 500;
                  letter-spacing: -0.28px;
                  line-height: 140%; /* 19.6px */
                  text-align: center;
                `}
              >
                Start a chat with your friends! <br />
                Share the link below and enjoy chatting together
              </p>
            </FlexStack>
            <FlexStack
              direction='row'
              css={css`
                align-items: center;
                justify-content: space-between;
                width: 100%;
                border: 1px solid var(--stroke_light, rgba(255, 255, 255, 0.2));
                border-radius: 8px;
                background: var(--bg_light_5_alpha, rgba(255, 255, 255, 0.2));
                backdrop-filter: blur(50px);
              `}
            >
              <ShareLinkText>{InAppLink}</ShareLinkText>
              <IconButtonWrapper
                css={css`
                  flex-shrink: 0;
                `}
                onClick={handleAddressCopy}
              >
                <Icon iconName={copySuccess ? 'Check' : 'Link'} />
              </IconButtonWrapper>
            </FlexStack>

            <FlexStack
              css={css`
                width: 100%;
              `}
            >
              <DefaultButton onClick={handleShareClick}>Share</DefaultButton>
              <DefaultButton
                colorType='black'
                onClick={() => setShowChatShareOpen(false)}
                isBlocked
              >
                Cancel
              </DefaultButton>
            </FlexStack>
          </Wrapper>
        </Container>
      )}
    </>
  );
}

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: ${theme.colors.blackAlpha30};
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 348px;
    padding: 16px;
    border-radius: 16px;
    background: ${theme.backgroundColor.bg_light_5_alpha};
    backdrop-filter: blur(50px);
    gap: 24px;
  `}
`;

const ShareLinkText = styled.p`
  ${({ theme }) => css`
    padding: 8px;
    ${theme.mixins.text.ellipsis_1};
  `}
`;
